import { Box, Skeleton, Slider, Typography } from "@mui/material";

interface Props {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  /** Valor ya formateado que se muestra en grande sobre el slider (`$3,000,000.00`, `24`). */
  valueText: string;
  /** Unidad junto al valor (`MXN`, `meses`). */
  unit: string;
  minLabel: string;
  maxLabel: string;
  /** Mientras carga la config que define el rango, el slider se reemplaza por un skeleton. */
  loading: boolean;
  testId: string;
}

/**
 * Replica `.quote-amount` + `.slider-track` + `.slider-labels` del cotizador en el diseño: el valor
 * elegido en grande, el slider en `primary.accent` y los extremos del rango debajo.
 */
export const SliderCotizador = ({
  value,
  onChange,
  min,
  max,
  step,
  valueText,
  unit,
  minLabel,
  maxLabel,
  loading,
  testId,
}: Props) => (
  <Box>
    <Typography
      sx={{
        // 28px del spec: queda entre `h3` (25) y `h2` (31) de la escala del theme.
        fontSize: 28,
        fontWeight: 600,
        letterSpacing: "-0.02em",
        lineHeight: 1,
        fontVariantNumeric: "tabular-nums",
        color: "text.primary",
        mb: 1.5,
      }}
    >
      {valueText}
      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75, letterSpacing: 0 }}>
        {unit}
      </Typography>
    </Typography>

    {loading ? (
      <Skeleton variant="rounded" animation="wave" height={6} sx={{ my: "13px" }} />
    ) : (
      <Slider
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(_event, newValue) => onChange(newValue as number)}
        slotProps={{ input: { "data-testid": testId } as React.InputHTMLAttributes<HTMLInputElement> }}
        sx={{
          height: 6,
          py: "10px",
          "& .MuiSlider-rail": { bgcolor: "#E4E7EC", opacity: 1 },
          "& .MuiSlider-track": { bgcolor: "primary.accent", border: "none" },
          "& .MuiSlider-thumb": {
            width: 18,
            height: 18,
            bgcolor: "background.paper",
            border: "2px solid",
            borderColor: "primary.accent",
            boxShadow: "0 1px 2px rgba(0,0,0,.10)",
            "&:hover, &.Mui-focusVisible, &.Mui-active": {
              boxShadow: (theme) => `0 0 0 6px ${theme.palette.primary.base}`,
            },
          },
        }}
      />
    )}

    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
      <Typography variant="body3" color="text.secondary">
        {minLabel}
      </Typography>
      <Typography variant="body3" color="text.secondary">
        {maxLabel}
      </Typography>
    </Box>
  </Box>
);
